/**
 * Wire types for file attachments on the content-review thread, and the
 * server's file policy — the one source of which files may be attached, how
 * large they may be, and how many one message may carry.
 */

/** The six file kinds the server classifies an upload into — drives how the
 *  chat bubble and the file viewer render an attachment. */
export type ThreadFileKind =
  | 'image'
  | 'video'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation';

/** One file type the server accepts: the extension a file is picked by, the
 *  content type the server stores it as (never trusted from the client), and
 *  its kind. */
export interface ThreadFilePolicyType {
  /** With its dot, lowercase: `.pdf`. */
  ext: string;
  content_type: string;
  kind: ThreadFileKind;
}

/**
 * The server's upload rules for the content thread —
 * `GET /roster/{entry_id}/threads/content/file-policy`. The client checks
 * picks and captured frames against these before uploading; the server
 * enforces them again.
 */
export interface ThreadFilePolicy {
  /** Largest file the upload endpoint takes, in bytes. */
  max_file_bytes: number;
  /** Files one message may carry. A moment's captured frame rides on top of these. */
  max_files_per_message: number;
  /** Largest captured frame a message may carry, in bytes. */
  max_frame_bytes: number;
  types: ThreadFilePolicyType[];
}

const EXTENSION_SEPARATOR = '.';
const NOT_FOUND = -1;
const NO_EXTENSION = '';
const ACCEPT_SEPARATOR = ',';

/** The substring of `fileName` from (and including) its last `.`, lowercased.
 *  A name with no `.` yields `''`, which never matches a policy type. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(EXTENSION_SEPARATOR);
  if (dot === NOT_FOUND) return NO_EXTENSION;
  return fileName.slice(dot).toLowerCase();
}

/**
 * The policy type a picked file attaches as, looked up by its extension,
 * case-insensitive. Undefined for anything the policy doesn't list — callers
 * refuse the file.
 */
export function threadFileTypeFor(policy: ThreadFilePolicy, fileName: string): ThreadFilePolicyType | undefined {
  const ext = extensionOf(fileName);
  return policy.types.find((type) => type.ext.toLowerCase() === ext);
}

/** `<input accept>` for a file picker: every extension the policy allows, comma-joined. */
export function threadFileAccept(policy: ThreadFilePolicy): string {
  return policy.types.map((type) => type.ext).join(ACCEPT_SEPARATOR);
}

/**
 * One uploaded file as the server returns it from
 * `POST /roster/{entry_id}/threads/content/files` — mirrors the inner
 * `data` of the `DataResponse` envelope.
 */
export interface ThreadFileView {
  id: string;
  name: string;
  kind: ThreadFileKind;
  content_type: string;
  size: number;
  width?: number;
  height?: number;
}

/**
 * One attachment as it rides along a message — mirrors the
 * `MessageView.attachments[]` wire shape (`file_id`, not `id`).
 */
export interface MessageAttachment {
  file_id: string;
  name: string;
  kind: ThreadFileKind;
  content_type: string;
  size: number;
  width?: number;
  height?: number;
}

/** What a chat entry carries: server attachments, or an optimistic one with
 *  a local object URL (set client-side while the upload is in flight —
 *  never sent to, or read from, the wire). */
export interface EntryAttachment extends MessageAttachment {
  local_url?: string;
}
